// pylee/apps/web/app/registry-mobile/components/registry-visualization.tsx
/**
 * Registry Visualization Component
 * 
 * This component visualizes PyleeAI as a collaborative platform for building,
 * sharing, and using AI tools with minimal friction. The central element
 * maintains the arch/omega shape that represents a secure gateway.
 */

'use client';

import { 
  // Icons used throughout the visualization
  Server, 
  Shield, 
  Check, 
  Code, 
  Database, 
  Cloud, 
  Users, 
  Search, 
  Download, 
  Upload, 
  Library,
  // Additional icons
  Smartphone,
  Globe,
  Layers,
  GitBranch,
  FileCode,
  BarChart,
  PieChart,
  Code2,
  Repeat,
  FileText,
  LockKeyhole,
  ArrowRightLeft,
  Table,
  Bot,
  Zap,
  MessageSquare,
  Omega // Added Omega icon for the brand title
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import styles from './registry-visualization.module.css';

/**
 * Provider Logo Component
 * Renders different icons based on provider type
 */
const ProviderLogo = ({ type }: { type: string }) => {
  switch (type) {
    case 'salesforce':
      return <Cloud size={12} strokeWidth={2.5} />; // Increased stroke width for better visibility
    case 'slack':
      return <MessageSquare size={12} strokeWidth={2.5} />;
    case 'postgres':
      return <Database size={12} strokeWidth={2.5} />;
    case 'openai':
      return <Bot size={12} strokeWidth={2.5} />;
    case 'zapier':
      return <Zap size={12} strokeWidth={2.5} />;
    default:
      return <Code size={12} strokeWidth={2.5} />;
  }
};

/**
 * Main Registry Visualization Component
 */
export default function RegistryVisualization() {
  // State for active provider (for demo animation)
  const [activeProvider, setActiveProvider] = useState('');
  
  // Ref for background animation canvas
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Cycle through active providers for animation effect
  useEffect(() => {
    const providers = ['salesforce', 'slack', 'postgres', 'openai', 'zapier'];
    let index = 0;
    
    const interval = setInterval(() => {
      setActiveProvider(providers[index]);
      index = (index + 1) % providers.length;
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  /**
   * ==========================================================================
   * SECTION 1: BACKGROUND CANVAS ANIMATION
   * Animated particle background that creates a tech/space feel
   * ==========================================================================
   */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions to match container
    const resizeCanvas = () => {
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = width;
      canvas.height = height;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Create particles for background effect
    const particles: {
      x: number;
      y: number;
      size: number;
      speedX: number;
      speedY: number;
      opacity: number;
      color: string;
    }[] = [];

    // Initialize particles with random properties
    for (let i = 0; i < 50; i++) {
      // Changed to use only blue tones instead of black
      const colorOptions = [
        'rgba(0, 191, 255, 0.8)',  // Light blue
        'rgba(64, 96, 255, 0.8)',   // Blue-purple
        'rgba(127, 0, 255, 0.6)',   // Purple
        'rgba(0, 210, 255, 0.7)',   // Cyan
        'rgba(0, 150, 255, 0.7)'    // Medium blue
      ];
      
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.7 + 0.3,
        color: colorOptions[Math.floor(Math.random() * colorOptions.length)]
      });
    }

    // Animation loop
    let animationFrameId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw grid lines
      ctx.strokeStyle = 'rgba(0, 191, 255, 0.05)';
      ctx.lineWidth = 1;
      
      // Vertical lines
      for (let x = 0; x < canvas.width; x += 20) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      // Horizontal lines
      for (let y = 0; y < canvas.height; y += 20) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
      
      // Update and draw particles
      particles.forEach((particle) => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        // Wrap around edges
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        
        // Draw particle
        ctx.fillStyle = particle.color;
        ctx.globalAlpha = particle.opacity;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
      });
      
      ctx.globalAlpha = 1;
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  /**
   * ==========================================================================
   * MAIN RENDER FUNCTION
   * ==========================================================================
   */
  return (
    <div className={styles.visualizationContainer}>
      {/* Background canvas with particles */}
      <canvas ref={canvasRef} className={styles.backgroundCanvas}></canvas>
      
      {/* Added wrapper for handling mobile scaling */}
      <div className={styles.registryWrapperContainer}>
        {/* Main visualization wrapper */}
        <div className={styles.registryWrapper}>
          {/* Background glow effect */}
          <div className={styles.glowingBackground}></div>
          
          {/* 
           * =======================================================================
           * LEFT SECTION: DEVELOPERS
           * Represents individuals and teams creating AI tools
           * =======================================================================
           */}
          <div className={styles.sideSection + ' ' + styles.leftSection}>
            {/* Developer icons */}
            <div className={styles.iconGroup}>
              <div className={styles.iconContainer}>
                <Code2 className={styles.icon} />
                <div className={styles.iconIndicator}></div>
              </div>
              <div className={styles.iconContainer}>
                <Users className={styles.icon} />
                <div className={styles.iconIndicator + ' ' + styles.secondaryIndicator}></div>
              </div>
            </div>
            
            {/* Template icons */}
            <div className={styles.iconGroup}>
              <div className={styles.iconContainer}>
                <FileText className={styles.icon} />
                <div className={styles.iconIndicator + ' ' + styles.tertiaryIndicator}></div>
              </div>
              <div className={styles.iconContainer}>
                <FileCode className={styles.icon} />
                <div className={styles.iconIndicator + ' ' + styles.quaternaryIndicator}></div>
              </div>
            </div>
            
            {/* Section label */}
            <div className={styles.sectionLabel}>
              <h3>Developers</h3>
              <p>Create & share AI tools</p>
            </div>
            
            {/* Config blocks with provider logos */}
            <div className={styles.configBlock + ' ' + styles.incomingConfig + ' ' + 
                 (activeProvider === 'salesforce' ? styles.activeConfigBlock : '')}>
              <div className={styles.configCore}></div>
              <div className={styles.providerLogo} data-provider="salesforce">
                <ProviderLogo type="salesforce" />
              </div>
            </div>
            
            <div className={styles.configBlock + ' ' + styles.outgoingConfig + ' ' + 
                 (activeProvider === 'slack' ? styles.activeConfigBlock : '')}>
              <div className={styles.configCore + ' ' + styles.quaternaryCore}></div>
              <div className={styles.providerLogo} data-provider="slack">
                <ProviderLogo type="slack" />
              </div>
              <div className={styles.verifiedBadge}>
                <Check size={10} />
              </div>
            </div>
          </div>
          
          {/* 
           * =======================================================================
           * LEFT FLOW CONNECTORS
           * Share/Clone connectors with arrows that rotate on smaller screens
           * =======================================================================
           */}
          <div className={styles.flowConnector + ' ' + styles.leftFlowConnector}>
            <div className={styles.flowIndicator}>
              <Upload className={styles.flowIcon} />
              <span>Share</span>
              <div className={`${styles.arrowRight} ${styles.primaryArrow}`}></div>
            </div>
            
            <div className={styles.flowIndicator}>
              <div className={`${styles.arrowLeft} ${styles.quaternaryArrow}`}></div>
              <GitBranch className={styles.flowIcon} />
              <span>Clone</span>
            </div>
            
            <div className={styles.flowIndicator}>
              <div className={`${styles.arrowLeft} ${styles.tertiaryArrow}`}></div>
              <Repeat className={styles.flowIcon} />
              <span>Fork</span>
            </div>
          </div>
          
          {/* 
           * =======================================================================
           * CENTER SECTION: PYLEE REGISTRY
           * Visualized as an arch/omega shape that acts as a gateway
           * =======================================================================
           */}
          <div className={styles.centerSection}>
            {/* Added PYLEE Brand Title above the registry */}
            <div className={styles.brandTitle}>
              <Omega size={24} strokeWidth={2.5} />
              <h2>PYLEE<span>AI</span></h2>
            </div>
            
            {/* Main portal/gateway structure */}
            <div className={styles.registry}>
              {/* Portal glow effect */}
              <div className={styles.registryPortal}></div>
              
              {/* Title at top of arch - "Registry" moved from Pylee Registry */}
              <div className={styles.registryHeader}>
                <Layers size={22} />
                <span>Private Registry</span>
              </div>
              
              {/* Portal particle effect */}
              <div className={styles.particleStream}></div>
              
              {/* Registry grid with provider-themed items - removed conflicting grid background */}
              <div className={styles.registryGrid}>
                {/* Salesforce-themed item */}
                <div className={styles.gridItem + ' ' + 
                     (activeProvider === 'salesforce' ? styles.activeGridItem : '')}>
                  <div className={styles.gridItemInner + ' ' + styles.primaryItem}>
                    <Cloud size={14} />
                  </div>
                </div>
                
                {/* Slack-themed item */}
                <div className={styles.gridItem + ' ' + 
                     (activeProvider === 'slack' ? styles.activeGridItem : '')}>
                  <div className={styles.gridItemInner + ' ' + styles.secondaryItem}>
                    <ArrowRightLeft size={14} />
                  </div>
                </div>
                
                {/* Postgres-themed item */}
                <div className={styles.gridItem + ' ' + 
                     (activeProvider === 'postgres' ? styles.activeGridItem : '')}>
                  <div className={styles.gridItemInner + ' ' + styles.tertiaryItem}>
                    <Table size={14} />
                  </div>
                </div>
                
                {/* Generic template item */}
                <div className={styles.gridItem}>
                  <div className={styles.gridItemInner + ' ' + styles.quaternaryItem}>
                    <FileText size={14} />
                  </div>
                </div>
                
                {/* Security icon */}
                <div className={styles.gridItem + ' ' + 
                     (activeProvider === 'openai' ? styles.activeGridItem : '')}>
                  <div className={styles.gridItemInner + ' ' + styles.primaryItem}>
                    <LockKeyhole size={14} />
                  </div>
                </div>
                
                {/* Bot item */}
                <div className={styles.gridItem + ' ' + 
                     (activeProvider === 'zapier' ? styles.activeGridItem : '')}>
                  <div className={styles.gridItemInner + ' ' + styles.secondaryItem}>
                    <Bot size={14} />
                  </div>
                </div>
                
                {/* Database item */}
                <div className={styles.gridItem}>
                  <div className={styles.gridItemInner + ' ' + styles.tertiaryItem}>
                    <Database size={14} />
                  </div>
                </div>
                
                {/* Search area */}
                <div className={`${styles.gridItem} ${styles.searchArea}`}>
                  <Search size={16} />
                </div>
              </div>
              
              {/* Status indicator - "Build & Share" */}
              <div className={styles.registryIndicator}>
                <Shield size={12} />
                <span>Build, Share, Discover</span>
              </div>
            </div>
            
            {/* Base of the portal/gateway */}
            <div className={styles.registryBase}></div>
            
            {/* Center section label - Updated with Shield icon */}
            <div className={styles.centerLabel}>
              <h3>
                <Shield size={16} />
                Secure Gateway
              </h3>
              <p>Control access to your MCP and AI Agents</p>
            </div>
          </div>
          
          {/* 
           * =======================================================================
           * RIGHT FLOW CONNECTORS
           * Deploy/Connect connectors with arrows that rotate on smaller screens
           * =======================================================================
           */}
          <div className={styles.flowConnector + ' ' + styles.rightFlowConnector}>
            <div className={styles.flowIndicator}>
              <div className={`${styles.arrowRight} ${styles.tertiaryArrow}`}></div>
              <Upload className={styles.flowIcon} />
              <span>Deploy</span>
            </div>
            
            <div className={styles.flowIndicator}>
              <span>Connect</span>
              <ArrowRightLeft className={styles.flowIcon} />
              <div className={`${styles.arrowLeft} ${styles.secondaryArrow}`}></div>
            </div>
            
            <div className={styles.flowIndicator}>
              <span>Sync</span>
              <Repeat className={styles.flowIcon} />
              <div className={`${styles.arrowLeft} ${styles.quaternaryArrow}`}></div>
            </div>
          </div>
          
          {/* 
           * =======================================================================
           * RIGHT SECTION: YOUR PROJECTS & APPS
           * Represents the environments where AI tools are used
           * =======================================================================
           */}
          <div className={styles.sideSection + ' ' + styles.rightSection}>
            {/* Web/Mobile icons */}
            <div className={styles.iconGroup}>
              <div className={styles.iconContainer}>
                <Globe className={styles.icon} />
                <div className={styles.iconIndicator}></div>
              </div>
              <div className={styles.iconContainer}>
                <Smartphone className={styles.icon} />
                <div className={styles.iconIndicator + ' ' + styles.secondaryIndicator}></div>
              </div>
            </div>
            
            {/* Server/Analytics icons */}
            <div className={styles.iconGroup}>
              <div className={styles.iconContainer}>
                <Server className={styles.icon} />
                <div className={styles.iconIndicator + ' ' + styles.tertiaryIndicator}></div>
              </div>
              <div className={styles.iconContainer}>
                <PieChart className={styles.icon} />
                <div className={styles.iconIndicator + ' ' + styles.quaternaryIndicator}></div>
              </div>
            </div>
            
            {/* Section label */}
            <div className={styles.sectionLabel}>
              <h3>Your Projects &amp; Apps</h3>
              <p>Use MCP servers across clients & environments</p>
            </div>
            
            {/* Config blocks with provider logos */}
            <div className={styles.configBlock + ' ' + styles.incomingConfig + ' ' + styles.rightConfig + ' ' + 
                 (activeProvider === 'postgres' ? styles.activeConfigBlock : '')}>
              <div className={styles.configCore + ' ' + styles.tertiaryCore}></div>
              <div className={styles.providerLogo} data-provider="postgres">
                <ProviderLogo type="postgres" />
              </div>
              <div className={styles.verifiedBadge}>
                <Check size={10} />
              </div>
            </div>
            
            <div className={styles.configBlock + ' ' + styles.outgoingConfig + ' ' + styles.rightConfig + ' ' + 
                 (activeProvider === 'openai' ? styles.activeConfigBlock : '')}>
              <div className={styles.configCore + ' ' + styles.secondaryCore}></div>
              <div className={styles.providerLogo} data-provider="openai">
                <ProviderLogo type="openai" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* 
       * =======================================================================
       * FEATURE CALLOUTS
       * Tags highlighting key features
       * =======================================================================
       */}
      <div className={styles.featureCallouts}>
        <div className={styles.featureTag}>
          <div className={styles.featureIndicator}></div>
          <span>Templates &amp; Vibes</span>
        </div>
        <div className={styles.featureTag}>
          <div className={styles.featureIndicator + ' ' + styles.tertiaryIndicator}></div>
          <span>Collaborative Workspace</span>
        </div>
        <div className={styles.featureTag}>
          <div className={styles.featureIndicator + ' ' + styles.quaternaryIndicator}></div>
          <span>Quick Integration</span>
        </div>
      </div>
    </div>
  );
}